import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { toast } from "sonner";
import {
  TrendingUp,
  TrendingDown,
  Truck,
  Calendar,
  Target,
  CheckCircle2,
  AlertCircle,
  XCircle,
  RotateCcw,
  Save,
  Settings as SettingsIcon,
  Wand2,
} from "lucide-react";

const STATUS_BG = {
  draft: "bg-amber-100 text-amber-700",
  finalized: "bg-emerald-100 text-emerald-700",
  reversed: "bg-red-100 text-red-700",
};

function curMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function prevMonth() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function FuelSettlements() {
  const [month, setMonth] = useState(prevMonth());
  const [view, setView] = useState("settled"); // "preview" | "settled"
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [finalizeId, setFinalizeId] = useState(null);
  const [finalizeNote, setFinalizeNote] = useState("");
  const [reverseId, setReverseId] = useState(null);
  const [reverseReason, setReverseReason] = useState("");

  const loadSettings = async () => {
    try {
      const { data } = await api.get("/fuel/settlements/settings");
      setSettings(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const loadList = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/fuel/settlements", { params: { month } });
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  const loadPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/fuel/settlements/preview", { params: { month } });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSettings(); }, []);
  useEffect(() => {
    if (view === "preview") loadPreview(); else loadList();
  }, [view, month]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    if (!window.confirm(`Generate draft settlements for ${month}? This will compute fresh values from all approved refills.`)) return;
    try {
      const { data } = await api.post("/fuel/settlements/generate", { month });
      toast.success(`Created: ${data.created}, Refreshed: ${data.refreshed}, Skipped (finalized): ${data.skipped_finalized}`);
      setView("settled");
      loadList();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const finalize = async () => {
    try {
      await api.post(`/fuel/settlements/${finalizeId}/finalize`, { note: finalizeNote || null });
      toast.success("Finalized — posted to next month payroll");
      setFinalizeId(null); setFinalizeNote("");
      loadList();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const reverse = async () => {
    if ((reverseReason || "").trim().length < 3) {
      toast.warning("Please enter a reason");
      return;
    }
    try {
      await api.post(`/fuel/settlements/${reverseId}/reverse`, { reason: reverseReason });
      toast.success("Reversed");
      setReverseId(null); setReverseReason("");
      loadList();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveSettings = async () => {
    try {
      await api.post("/fuel/settlements/settings", settings);
      toast.success("Settings saved");
      setShowSettings(false);
      loadSettings();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const totalIncentive = rows.filter((r) => r.settlement_amount > 0).reduce((s, r) => s + r.settlement_amount, 0);
  const totalPenalty = rows.filter((r) => r.settlement_amount < 0).reduce((s, r) => s + Math.abs(r.settlement_amount), 0);
  const finalizedCount = rows.filter((r) => r.status === "finalized").length;
  const draftCount = rows.filter((r) => r.status === "draft").length;
  const dataset = view === "preview" ? (preview?.rows || []) : rows;

  return (
    <div className="space-y-4" data-testid="fuel-settlements">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-bravura"
            data-testid="settle-month-picker"
          />
          <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
            <button
              onClick={() => setView("settled")}
              data-testid="settle-tab-settled"
              className={`px-3 py-1.5 rounded-md text-xs font-semibold ${view === "settled" ? "bg-white text-slate-900 shadow" : "text-slate-500"}`}
            >Settlements</button>
            <button
              onClick={() => setView("preview")}
              data-testid="settle-tab-preview"
              className={`px-3 py-1.5 rounded-md text-xs font-semibold ${view === "preview" ? "bg-white text-slate-900 shadow" : "text-slate-500"}`}
            >Preview</button>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={generate}
            data-testid="settle-generate-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-bravura text-white text-xs font-semibold"
          >
            <Wand2 className="h-3.5 w-3.5" /> Generate Drafts
          </button>
          <button
            onClick={() => setShowSettings(true)}
            data-testid="settle-settings-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-100 text-slate-700 text-xs font-semibold"
          >
            <SettingsIcon className="h-3.5 w-3.5" /> Settings
          </button>
        </div>
      </div>

      {settings && (
        <div className="text-[11px] text-slate-500">
          Target: <b className="text-slate-900">{settings.target_kmpl_default} km/L</b> · Auto-finalize day: <b className="text-slate-900">{settings.auto_finalize_day} of next month</b>
        </div>
      )}

      {view === "settled" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Total Incentive" value={formatINR(totalIncentive)} Icon={TrendingUp} color="emerald" />
          <Kpi label="Total Penalty" value={formatINR(totalPenalty)} Icon={TrendingDown} color="rose" />
          <Kpi label="Finalized" value={finalizedCount} Icon={CheckCircle2} color="sky" />
          <Kpi label="Draft" value={draftCount} Icon={AlertCircle} color="amber" />
        </div>
      )}

      <div className="rounded-xl bg-white ring-1 ring-slate-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Driver</th>
              <th className="text-left px-3 py-2">Truck</th>
              <th className="text-right px-3 py-2">Target</th>
              <th className="text-right px-3 py-2">Litres</th>
              <th className="text-right px-3 py-2">Expected km</th>
              <th className="text-right px-3 py-2">Actual km</th>
              <th className="text-right px-3 py-2">Diff (L)</th>
              <th className="text-right px-3 py-2">Settlement</th>
              <th className="text-center px-3 py-2">Status</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="text-center text-xs text-slate-500 py-6">Loading…</td></tr>}
            {!loading && dataset.length === 0 && (
              <tr><td colSpan={10} className="text-center text-xs text-slate-400 py-8">
                {view === "preview"
                  ? `No refill data found for ${month}. Generate refills first.`
                  : `No settlements for ${month}. Click "Generate Drafts" to create.`}
              </td></tr>
            )}
            {dataset.map((r) => (
              <tr key={r.id || r.driver_id} className="border-t border-slate-100">
                <td className="px-3 py-2">
                  <div className="font-semibold text-slate-900">{r.driver_name || "—"}</div>
                  <div className="text-[10px] text-slate-500">{r.driver_code || ""}</div>
                </td>
                <td className="px-3 py-2 text-xs">{r.primary_truck?.truck_no || "—"}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{r.target_kmpl} km/L</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{r.total_litres} L</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{r.expected_km}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{r.actual_km}</td>
                <td className={`px-3 py-2 text-right font-mono text-xs font-bold ${r.diff_litres > 0 ? "text-emerald-700" : r.diff_litres < 0 ? "text-red-700" : "text-slate-500"}`}>
                  {r.diff_litres > 0 ? "+" : ""}{r.diff_litres} L
                </td>
                <td className={`px-3 py-2 text-right font-bold ${r.settlement_amount > 0 ? "text-emerald-700" : r.settlement_amount < 0 ? "text-red-700" : "text-slate-500"}`}>
                  {r.settlement_amount > 0 ? "+" : ""}{formatINR(r.settlement_amount)}
                </td>
                <td className="px-3 py-2 text-center">
                  {view === "preview" ? (
                    r.already_settled ? (
                      <span className="text-[10px] text-emerald-700 bg-emerald-50 rounded-full px-1.5 py-0.5">In DB</span>
                    ) : (
                      <span className="text-[10px] text-slate-500">New</span>
                    )
                  ) : (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${STATUS_BG[r.status]}`}>
                      {r.status?.toUpperCase()}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  {view === "settled" && r.status === "draft" && (
                    <button
                      onClick={() => setFinalizeId(r.id)}
                      data-testid={`finalize-${r.id}`}
                      className="text-bravura text-xs font-bold inline-flex items-center gap-1"
                    >
                      <Save className="h-3 w-3" /> Finalize
                    </button>
                  )}
                  {view === "settled" && r.status === "finalized" && (
                    <button
                      onClick={() => setReverseId(r.id)}
                      data-testid={`reverse-${r.id}`}
                      className="text-red-600 text-xs font-bold inline-flex items-center gap-1"
                    >
                      <RotateCcw className="h-3 w-3" /> Reverse
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {finalizeId && (
        <Modal title="Finalize Settlement" onClose={() => { setFinalizeId(null); setFinalizeNote(""); }}>
          <p className="text-xs text-slate-600">
            Finalize karne par next month ki <b>draft payslip</b> mein incentive/deduction add ho jayega.
            Yeh action sirf super_admin reverse kar sakta hai.
          </p>
          <textarea
            rows={2}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            placeholder="Optional note"
            value={finalizeNote}
            onChange={(e) => setFinalizeNote(e.target.value)}
            data-testid="finalize-note"
          />
          <div className="flex gap-2">
            <button onClick={() => { setFinalizeId(null); setFinalizeNote(""); }} className="flex-1 rounded-lg bg-slate-100 py-2 text-sm font-semibold">Cancel</button>
            <button onClick={finalize} data-testid="confirm-finalize" className="flex-1 rounded-lg bg-emerald-600 text-white py-2 text-sm font-semibold">Finalize & Post</button>
          </div>
        </Modal>
      )}

      {reverseId && (
        <Modal title="Reverse Settlement" onClose={() => { setReverseId(null); setReverseReason(""); }}>
          <p className="text-xs text-red-700 bg-red-50 rounded-lg p-2">
            ⚠ This will roll back the payroll adjustment (if draft) and mark the settlement as reversed.
          </p>
          <textarea
            rows={3}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            placeholder="Reason (required)"
            value={reverseReason}
            onChange={(e) => setReverseReason(e.target.value)}
            data-testid="reverse-reason-input"
          />
          <div className="flex gap-2">
            <button onClick={() => { setReverseId(null); setReverseReason(""); }} className="flex-1 rounded-lg bg-slate-100 py-2 text-sm font-semibold">Cancel</button>
            <button onClick={reverse} data-testid="confirm-reverse" className="flex-1 rounded-lg bg-red-600 text-white py-2 text-sm font-semibold">Reverse</button>
          </div>
        </Modal>
      )}

      {showSettings && settings && (
        <Modal title="Settlement Settings" onClose={() => setShowSettings(false)}>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">Target km/L (global default)</label>
            <input
              type="number" step="0.1"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              value={settings.target_kmpl_default}
              onChange={(e) => setSettings({ ...settings, target_kmpl_default: parseFloat(e.target.value) || 0 })}
              data-testid="set-target-kmpl"
            />
            <p className="text-[10px] text-slate-500 mt-1">Per-truck override via Truck Master.</p>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">Auto-finalize day</label>
            <input
              type="number" min="1" max="15"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              value={settings.auto_finalize_day}
              onChange={(e) => setSettings({ ...settings, auto_finalize_day: parseInt(e.target.value, 10) || 4 })}
              data-testid="set-auto-finalize-day"
            />
            <p className="text-[10px] text-slate-500 mt-1">Anything still in draft on this day of next month is auto-finalized.</p>
          </div>
          <button onClick={saveSettings} data-testid="save-settings-btn" className="w-full rounded-lg bg-bravura text-white py-2.5 text-sm font-bold">
            Save
          </button>
        </Modal>
      )}
    </div>
  );
}

function Kpi({ label, value, Icon, color }) {
  const map = {
    emerald: "from-emerald-50 ring-emerald-200 text-emerald-700",
    rose: "from-red-50 ring-red-200 text-red-700",
    sky: "from-sky-50 ring-sky-200 text-sky-700",
    amber: "from-amber-50 ring-amber-200 text-amber-700",
  };
  return (
    <div className={`rounded-xl ring-1 bg-gradient-to-br to-white p-3 ${map[color]}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="text-xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Chivo" }}>
        {value}
      </div>
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl p-5 space-y-3 shadow-xl">
        <div className="flex items-center justify-between">
          <h4 className="text-base font-bold">{title}</h4>
          <button onClick={onClose}><XCircle className="h-4 w-4 text-slate-400" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
